import {
	fits,
	MARKET_SIZE,
	type PlayerIndex,
	SUITS,
	type SuitRunState,
	TOWERS,
	type TowerSwapState,
} from "@playpal/core";
import { useState } from "react";

interface BoardProps<S> {
	state: S;
	seat: PlayerIndex;
	onMove: (move: unknown) => void;
}

// --- Suit Run -------------------------------------------------------------

export function SuitRunBoard({ state, seat, onMove }: BoardProps<SuitRunState>) {
	const myTurn = state.turn === seat && !state.result;
	const them: PlayerIndex = seat === 0 ? 1 : 0;
	// Swap mode: the next market card tapped is traded for the top of the deck
	// instead of taken — spending the turn to ditch a card the other run needs.
	const [swapping, setSwapping] = useState(false);
	const canSwap = myTurn && state.deckLeft > 0;

	const run = (cards: SuitRunState["runs"][number], label: string, mine: boolean) => (
		<div className="mb-3">
			<p className="mb-1 text-sm font-bold opacity-70">
				{label} — {cards.length} long
			</p>
			<div className="flex flex-wrap gap-1">
				{cards.length === 0 && <span className="text-sm opacity-60">nothing yet</span>}
				{cards.map((card) => (
					<span
						key={`${card.suit}-${card.value}`}
						className={`flex h-12 w-9 flex-col items-center justify-center rounded-lg text-sm font-black shadow-sm ${
							mine ? "bg-white text-indigo-950" : "bg-white/60 text-indigo-900/70"
						}`}
					>
						<span>{SUITS[card.suit]}</span>
						<span>{card.value}</span>
					</span>
				))}
			</div>
		</div>
	);

	return (
		<div className="board-frame board-lg">
			<p className="mb-3 text-center text-lg font-semibold">{state.deckLeft} cards still to come</p>

			<div className="mb-2 flex justify-center gap-2">
				{Array.from({ length: MARKET_SIZE }, (_, slot) => {
					const card = state.market[slot];
					const takeable = myTurn && card !== null && fits(state.runs[seat], card);
					const swappable = swapping && canSwap && card !== null;
					const active = swapping ? swappable : takeable;
					return (
						<button
							key={slot}
							type="button"
							disabled={!active}
							aria-label={
								card
									? `${swapping ? "Swap out" : "Take"} ${SUITS[card.suit]} ${card.value}`
									: "Empty space"
							}
							onClick={() => {
								if (swapping) {
									onMove({ swap: slot });
									setSwapping(false);
								} else {
									onMove({ take: slot });
								}
							}}
							className={`flex h-24 w-16 flex-col items-center justify-center rounded-xl text-xl font-black shadow-md transition ${
								card === null
									? "bg-white/20"
									: swappable
										? "bg-amber-200 text-amber-950 ring-4 ring-amber-400 hover:-translate-y-1"
										: active
											? "bg-white text-indigo-950 hover:-translate-y-1"
											: "bg-white/50 text-indigo-900/50"
							}`}
						>
							{card && (
								<>
									<span className="text-2xl">{SUITS[card.suit]}</span>
									<span>{card.value}</span>
								</>
							)}
						</button>
					);
				})}
			</div>

			<div className="mb-4 flex justify-center">
				<button
					type="button"
					disabled={!canSwap}
					onClick={() => setSwapping((mode) => !mode)}
					className={`rounded-full px-4 py-1 text-sm font-bold shadow-sm transition ${
						swapping
							? "bg-amber-400 text-amber-950"
							: canSwap
								? "bg-white/70 text-indigo-950 hover:bg-white"
								: "bg-white/30 text-indigo-900/40"
					}`}
				>
					{swapping ? "Cancel swap" : "Swap a card instead"}
				</button>
			</div>

			{run(state.runs[seat], "Your run", true)}
			{run(state.runs[them], "Their run", false)}

			<p className="h-6 text-center font-semibold" aria-live="polite">
				{swapping
					? "Tap the card to swap out"
					: (state.lastNote ?? (myTurn ? "Take a card that carries your run on" : "Their turn…"))}
			</p>
		</div>
	);
}

// --- Tower Swap -----------------------------------------------------------

const CLAIM_COLOUR = ["bg-rose-400/70", "bg-sky-400/70"] as const;

export function TowerSwapBoard({ state, seat, onMove }: BoardProps<TowerSwapState>) {
	const myTurn = state.turn === seat && !state.result;
	const [from, setFrom] = useState<number | null>(null);

	const topOf = (tower: number[]) => (tower.length === 0 ? null : tower[tower.length - 1]);
	const canDrop = (to: number) => {
		if (from === null || from === to || state.claimed[to] !== null) return false;
		const block = topOf(state.towers[from]);
		const target = topOf(state.towers[to]);
		return block !== null && (target === null || block < target);
	};

	return (
		<div className="board-frame board-lg">
			<p className="mb-3 text-center text-lg font-semibold">
				Towers claimed — you {state.scores[seat]}, them {state.scores[seat === 0 ? 1 : 0]}
			</p>

			<div className="flex items-end justify-center gap-4">
				{state.towers.map((tower, index) => {
					const claimed = state.claimed[index];
					const selectable = myTurn && claimed === null && tower.length > 0;
					const droppable = myTurn && canDrop(index);

					return (
						<button
							key={index}
							type="button"
							disabled={!selectable && !droppable}
							aria-label={`Tower ${index + 1}, ${tower.length} blocks${
								claimed === null ? "" : ", claimed"
							}`}
							onClick={() => {
								if (droppable && from !== null) {
									onMove({ from, to: index });
									setFrom(null);
								} else if (selectable) {
									setFrom(index === from ? null : index);
								}
							}}
							className={`flex w-24 flex-col-reverse items-center gap-1 rounded-2xl p-2 pt-6 transition ${
								claimed !== null
									? CLAIM_COLOUR[claimed]
									: droppable
										? "bg-emerald-200 ring-4 ring-emerald-400 dark:bg-emerald-900/40"
										: index === from
											? "bg-indigo-200 ring-4 ring-indigo-400 dark:bg-indigo-900/40"
											: "bg-white/60 dark:bg-white/10"
							}`}
						>
							{tower.map((block, height) => (
								<span
									key={`${block}`}
									className="flex items-center justify-center rounded-md bg-amber-500 text-sm font-black text-amber-950 shadow-sm"
									style={{ width: `${30 + block * 4}%`, height: 18 }}
								>
									{height === tower.length - 1 ? block : ""}
								</span>
							))}
							{tower.length === 0 && <span className="text-sm opacity-60">empty</span>}
						</button>
					);
				})}
			</div>

			<p className="mt-4 h-6 text-center font-semibold" aria-live="polite">
				{state.lastNote ??
					(myTurn
						? from === null
							? "Tap a tower to pick up its top block"
							: "Now tap where it goes"
						: "Their turn…")}
			</p>
			<p className="mt-1 text-center text-sm opacity-70">
				{TOWERS} towers · put a 1 on top of one to claim it.
			</p>
		</div>
	);
}
